import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import IntellendLogo from '@/components/IntellendLogo';
import LoginForm from '@/components/LoginForm';
import { useLoader } from "@/contexts/LoaderContext";
import { API_BASE_URL } from '@/config';
import axios from "@/lib/axios";
import QRCode from "react-qr-code";
import JourneyHeader from "@/components/JourneyHeader";

const IndexWhitespace = () => {
  const ua = navigator.userAgent || "";
  const isMobile = /\bandroid\b|iphone|ipad|ipod|mobile/i.test(ua);
  const { loading, setLoading } = useLoader();
  const { enc_id } = useParams<{ enc_id: string }>();
  const navigate = useNavigate();
  const [bapLogo, setBapLogo] = useState<string | null>(null);
  const [bapId, setBapId] = useState<string | null>(null);

  const checkBapIdentifier = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/check_bap_identifier_active_session/`, {}, {
        withCredentials: true, // send HttpOnly cookie
      });
      // also check the response for the logo in case it was missing from sessionStorage
      if (res.data && res.data.bap_logo) {
        setBapLogo(res.data.bap_logo);
      }
      setLoading(false);
    } catch (err) {
      navigate('/home');
    }
  };

  useEffect(() => {
    // Check session storage for BAP details
    const sessionBapId = sessionStorage.getItem('whitespace_bap_id');
    const sessionBapLogo = sessionStorage.getItem('whitespace_bap_logo');

    let logoFoundInSession = false;

    if (sessionBapId) {
      setBapId(sessionBapId);
    }

    if (sessionBapLogo) {
      setBapLogo(sessionBapLogo);
      logoFoundInSession = true;
    }

    // Always check the identifier with the backend API
    // It might return the logo even if the session storage doesn't have it
    checkBapIdentifier();

  }, []);

  // CASE 1: Desktop/tablet (non-mobile) → Show QR screen
  if (!isMobile) {
    const currentUrl = window.location.href;
    return (
      <div className="fixed inset-0 bg-white flex flex-col justify-center items-center z-[9999]">
        <div className="flex flex-col items-center text-center px-4">
          <div className="flex gap-4 items-center justify-center mb-6">
            {bapLogo && (
              <img
                src={bapLogo}
                alt={`${bapId || 'BAP'} Logo`}
                className="h-10 w-auto object-contain"
                onError={(e) => {
                  (e.currentTarget as HTMLElement).style.display = 'none';
                }}
              />
            )}
            <IntellendLogo size="md" />
          </div>
          <h1 className="text-2xl font-semibold mb-2 text-gray-800">
            Continue on Your Phone
          </h1>
          <p className="text-gray-600 mb-6 max-w-sm">
            Scan this QR code using your mobile camera to continue securely on your phone.
          </p>
          <div className="bg-white p-4 rounded-xl shadow-lg">
            <QRCode value={currentUrl} size={220} />
          </div>
          <p className="text-sm text-gray-500 mt-4">
            This page can only be accessed from a mobile device.
          </p>
        </div>
      </div>
    );
  }

  // CASE 2: Mobile devices → Normal login flow
  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-intellend-white">
      {/* On mobile, show JourneyHeader */}
      <div className="md:hidden block">
        <JourneyHeader />
      </div>

      {/* Left side - Green background on desktop, top section on mobile */}
      <div className="md:w-1/2 bg-intellend-green p-6 md:p-10 flex flex-col justify-between">
        <div className="mb-8 md:mb-0">
          <div className="flex gap-4 items-center">
            <div className="bg-intellend-white inline-flex px-2 py-1 rounded-md">
              <IntellendLogo size="sm" />
            </div>
          </div>
          <h1 className="text-intellend-white text-2xl md:text-4xl font-bold mt-6 md:mt-20 max-w-md">
            Financial Solutions for Indian MSMEs
          </h1>
          <p className="text-intellend-white/80 mt-4 max-w-md">
            Access business loans tailored for your growth needs. Quick approvals, competitive rates, and flexible terms.
          </p>
        </div>
        <div className="md:block text-intellend-white/70 text-sm hidden">
          2026 Intellend. All rights reserved.
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="md:w-1/2 flex-1 flex items-center justify-center p-6 md:p-10 login-gradient">
        <div className="w-full max-w-md mt-6 md:mt-0">
          <LoginForm isWhitespace={true} />
          <div className="mt-8 text-center text-xs text-gray-500 hidden">
            2026 Intellend. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndexWhitespace;
